/* Design System Dependencies */
import vClickOutside from 'v-click-outside';
import PortalVue from 'portal-vue';
import VuejsClipper from 'vuejs-clipper';
import AirbnbStyleDatepicker from 'vue-airbnb-style-datepicker';
import VueScrollTo from 'vue-scrollto';
import ChartJS from './chart/mixins/chartPlugin';

/* Import Focus Visible */
import '../utils/accessibility/focus-visible.min';

/* Import Helio Mixins */
import Mixins from '../mixins';

/* Import our Components */
import HelioAvatar from './avatar/Avatar.vue';
import HelioAccordion from './accordion/Accordion.vue';
import HelioBadge from './badge/Badge.vue';
import HelioBanner from './banner/Banner.vue';
import HelioBarChart from './bar-chart/BarChart.vue';
import HelioBox from './box/Box.vue';
import HelioBreadcrumb from './breadcrumb/Breadcrumb.vue';
import HelioButton from './button/Button.vue';
import HelioButtonGroup from './button-group/ButtonGroup.vue';
import HelioCard from './card/Card.vue';
import HelioCards from './cards/Cards.vue';
import HelioCheckbox from './checkbox/Checkbox.vue';
import HelioCarousel from './carousel/Carousel.vue';
import HelioCollapsible from './collapsible/Collapsible.vue';
import HelioDatepicker from './datepicker/Datepicker.vue';
import HelioDefaultImage from './default-image/DefaultImage.vue';
import HelioDoughnutChart from './doughnut-chart/DoughnutChart.vue';
import HelioDropdown from './dropdown/Dropdown.vue';
import HelioDropdownGroup from './dropdown-group/DropdownGroup.vue';
import HelioEmpty from './empty/Empty.vue';
import HelioField from './field/Field.vue';
import HelioForm from './form/Form.vue';
import HelioHeader from './header-component/HeaderComponent.vue';
import HelioIcon from './icon/Icon.vue';
import HelioInformation from './information/Information.vue';
import HelioIntro from './intro/Intro.vue';
import HelioLineChart from './line-chart/LineChart.vue';
import HelioLink from './link/Link.vue';
import HelioModal from './modal/Modal.vue';
import HelioMessage from './message/Message.vue';
import HelioNotificationCount from './notification-count/NotificationCount.vue';
import HelioOverlay from './overlay/Overlay.vue';
import HelioPagination from './pagination/Pagination.vue';
import HelioPanel from './panel/Panel.vue';
import HelioPopup from './popup/Popup.vue';
import HelioPopconfirm from './popconfirm/Popconfirm.vue';
import HelioPost from './post/Post.vue';
import HelioProgressBar from './progress-bar/ProgressBar.vue';
import HelioProgressCircle from './progress-circle/ProgressCircle.vue';
import HelioRadio from './radio/Radio.vue';
import HelioRadioGroup from './radio-group/RadioGroup.vue';
import HelioRangeSlider from './range-slider/RangeSlider.vue';
import HelioResources from './resources/Resources.vue';
import HelioSeparator from './separator/Separator.vue';
import HelioSlider from './slider/Slider.vue';
import HelioSpinner from './spinner/Spinner.vue';
import HelioStatus from './status/Status.vue';
import HelioStickyBar from './sticky-bar/StickyBar.vue';
import HelioSwitch from './switch/Switch.vue';
import HelioTab from './tab/Tab.vue';
import HelioTable from './table/Table.vue';
import HelioTabs from './tabs/Tabs.vue';
import HelioTags from './tags/Tags.vue';
import HelioTimepicker from './timepicker/Timepicker.vue';
import HelioToast from './toast/Toast.vue';
import HelioTopbar from './topbar/Topbar.vue';
import HelioTooltip from './tooltip/Tooltip.vue';
import HelioUpload from './upload/Upload.vue';
import HelioUploadButton from './upload-button/UploadButton.vue';
import HelioVerticalNavigation from './vertical-navigation/VerticalNavigation.vue';
import HelioLostConnection from './lost-connection/LostConnection.vue';
import * as en from '../translations/en.json';
import * as fr from '../translations/fr.json';

const languages = {
	en,
	fr,
};

const datepickerColors = {
	selected: '#2Ea2f8',
	inRange: '#f5fafe',
	inRangeBorder: '#f5fafe',
	hoveredInRange: '#f5fafe',
	text: '#202c40',
	selectedText: '#202c40',
	disabled: '#202c40',
};

const components = {
	HelioAvatar,
	HelioAccordion,
	HelioBadge,
	HelioBanner,
	HelioBarChart,
	HelioBox,
	HelioBreadcrumb,
	HelioButton,
	HelioButtonGroup,
	HelioCard,
	HelioCards,
	HelioCheckbox,
	HelioCarousel,
	HelioCollapsible,
	HelioDatepicker,
	HelioDefaultImage,
	HelioDoughnutChart,
	HelioDropdown,
	HelioDropdownGroup,
	HelioEmpty,
	HelioField,
	HelioForm,
	HelioHeader,
	HelioIcon,
	HelioInformation,
	HelioIntro,
	HelioLineChart,
	HelioLink,
	HelioMessage,
	HelioModal,
	HelioNotificationCount,
	HelioOverlay,
	HelioPagination,
	HelioPanel,
	HelioPopup,
	HelioPopconfirm,
	HelioPost,
	HelioProgressBar,
	HelioProgressCircle,
	HelioRadio,
	HelioRadioGroup,
	HelioRangeSlider,
	HelioResources,
	HelioSeparator,
	HelioSlider,
	HelioSpinner,
	HelioStatus,
	HelioStickyBar,
	HelioSwitch,
	HelioTab,
	HelioTable,
	HelioTabs,
	HelioTags,
	HelioTimepicker,
	HelioToast,
	HelioTooltip,
	HelioTopbar,
	HelioUpload,
	HelioUploadButton,
	HelioVerticalNavigation,
	HelioLostConnection,
};

export default {
	install(Vue, options = {}) {
		Vue.use(vClickOutside);
		Vue.use(PortalVue);
		Vue.use(VuejsClipper);
		Vue.use(VueScrollTo);
		Vue.use(ChartJS);
		Vue.mixin(Mixins);

		const i18n = options.i18n ? options.i18n : null;

		// We merge the design system translations with the one from the app
		if (i18n !== null) {
			i18n.mergeLocaleMessage('en', languages.en.default);
			i18n.mergeLocaleMessage('fr', languages.fr.default);
		}

		const componentsToBind = options.components ? options.components : null;

		// We bind every components one by one. We only bind them if no specifics components
		// have been specified or if the component has been specified in the components list
		Object.keys(components).forEach((name) => {
			const prefix = options.prefix ? options.prefix : 'Helio';
			const shortName = name.replace('Helio', '');
			const prefixedName = name.replace('Helio', prefix);

			if (
				(!componentsToBind || componentsToBind.length === 0)
				|| (componentsToBind && componentsToBind.includes(shortName))
			) {
				Vue.component(prefixedName, components[name]);

				if (shortName === 'Datepicker' && i18n !== null) {
					Vue.use(AirbnbStyleDatepicker, {
						colors: datepickerColors,
					});
				}
			}
		});
	},
};
